import { createFileRoute, Link } from "../lib/router-compat";
import { PageShell, YouTubeEmbed } from "../components/PageShell";

export const Route = createFileRoute("/pan-coalition")({
  head: () => ({
    meta: [
      { title: "Pan Coalition — The Pan Institute" },
      { name: "description", content: "A coalition of steelpan players from Maine to Trinidad." },
    ],
  }),
  component: CoalitionPage,
});

const STOPS = [
  { city: "Brooksville", region: "Maine", note: "Home base · 150 Red Trail" },
  { city: "Boston", region: "Massachusetts", note: "Festivals & sit-ins" },
  { city: "New York", region: "Brooklyn", note: "Panorama · Steel Sensation" },
  { city: "Montreal", region: "Québec", note: "Summer circuit" },
  { city: "Toronto", region: "Ontario", note: "Caribana season" },
  { city: "Port of Spain", region: "Trinidad", note: "Where the pan was born" },
];

const VIDEOS = [
  { id: "xqIA8N1Y2ws", title: "NY Panorama 2025 — Steel Sensation Steel Orchestra", year: "2025", place: "Brooklyn, NY" },
  { id: "iDMmHjSMDsk", title: "Outta De Box", year: "—", place: "Pan Coalition" },
  { id: "PZipIzeKHMo", title: "Live in Blue Hill", year: "—", place: "blue hill maine" },
];

function CoalitionPage() {
  return (
    <PageShell
      panBackdrop={true}
      tag=""
      title={
        <>
          A coalition,<br />
          <span className="text-primary">on the road.</span>
        </>
      }
      intro="Players from Pan Institute's steel bands joined by musicians of all ages and backgrounds — Maine to Trinidad. We pack the pans into the truck and chase festivals, panoramas, and any stage that'll have us."
    >
      {/* Featured film — large */}
      <section className="mt-4">
        <div className="flex items-end justify-between gap-6 border-b border-border/40 pb-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary">Now playing · 2025</div>
            <h2 className="mt-3 font-display italic text-3xl md:text-5xl leading-[1.02]">
              Pan Coalition with Steel Sensation
            </h2>
          </div>
          <div className="hidden md:block text-[10px] uppercase tracking-[0.4em] text-foreground/45 whitespace-nowrap">
            NY Panorama / Brooklyn
          </div>
        </div>
        <YouTubeEmbed id={VIDEOS[0].id} title={VIDEOS[0].title} />
      </section>

      {/* Itinerary / tour stops */}
      <section className="mt-24">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-8">
          <div className="md:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">The itinerary</div>
            <h2 className="mt-3 font-display italic text-3xl md:text-5xl leading-[1.02]">
              Maine to Trinidad,<br /> and back again.
            </h2>
          </div>
          <div className="md:col-span-7 md:pb-3">
            <p className="text-sm md:text-base text-foreground/65 leading-relaxed max-w-xl md:ml-auto">
              Six cities on the regular rotation. Some we play every summer. Some we go to once and remember forever.
            </p>
          </div>
        </div>

        <ol className="border-t border-border/40">
          {STOPS.map((s, i) => (
            <li
              key={s.city}
              className="group grid grid-cols-12 gap-4 items-baseline border-b border-border/40 py-5 md:py-7 hover:bg-foreground/[0.02] transition"
            >
              <span className="col-span-2 md:col-span-1 font-display italic text-lg md:text-xl text-primary/50">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="col-span-7 md:col-span-5 font-display italic text-2xl md:text-4xl leading-none group-hover:text-primary transition">
                {s.city}
              </span>
              <span className="col-span-3 md:col-span-3 text-[10px] uppercase tracking-[0.3em] text-foreground/55">
                {s.region}
              </span>
              <span className="col-span-12 md:col-span-3 text-[13px] text-foreground/60 md:text-right">
                {s.note}
              </span>
            </li>
          ))}
        </ol>
      </section>

      {/* Sit in */}
      <section className="mt-24 border-y border-border/40 py-12 grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-7">
          <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">Sit in</div>
          <h2 className="mt-3 font-display italic text-3xl md:text-5xl leading-[1.02]">
            Bring your sticks.
          </h2>
          <p className="mt-5 text-foreground/65 max-w-md leading-relaxed">
            Players of all ages and backgrounds are welcome at the New Pan Facility in Brooksville. Reach out and we'll find you a pan.
          </p>
        </div>
        <div className="md:col-span-5 md:text-right">
          <div className="text-[10px] uppercase tracking-[0.4em] text-foreground/45">The room</div>
          <div className="mt-2 font-display italic text-2xl md:text-3xl">150 Red Trail</div>
          <div className="text-sm text-foreground/60">Brooksville, Maine</div>
          <Link
            to="/contact"
            className="mt-6 inline-flex items-center gap-4 text-[11px] tracking-[0.35em] uppercase text-primary hover:text-primary/70 transition"
          >
            <span className="h-px w-10 bg-primary" />
            Join the coalition
          </Link>
        </div>
      </section>

      {/* Archive */}
      <section className="mt-24">
        <div className="flex items-end justify-between gap-6 border-b border-border/40 pb-4 mb-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">From the road</div>
            <h2 className="mt-3 font-display italic text-3xl md:text-4xl">Field recordings</h2>
          </div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-foreground/40 whitespace-nowrap">
            {VIDEOS.length - 1} clips
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-10">
          {VIDEOS.slice(1).map((v, i) => (
            <article key={v.id}>
              <YouTubeEmbed id={v.id} title={v.title} />
              <div className="mt-4 flex items-baseline gap-4">
                <span className="font-display italic text-3xl text-primary/40 leading-none">
                  {String(i + 2).padStart(2, "0")}
                </span>
                <span className="h-px flex-1 bg-border/50" />
                <span className="text-[10px] uppercase tracking-[0.3em] text-foreground/45">
                  {v.year} · {v.place}
                </span>
              </div>
              <h3 className="mt-3 font-display italic text-2xl">{v.title}</h3>
            </article>
          ))}
        </div>
      </section>
    </PageShell>
  );
}
